import React, { useState, useEffect, useRef } from 'react';
import SettingsTabs from 'components/quote_builder/SettingsTabs.jsx';
import SettingsMenu from 'components/quote_builder/SettingsMenu.jsx';
import { GetRequest } from 'components/common';

const buildSettingsUrl = (section, tabKey) => {
  let url = '/quote-builder/settings';
  if (section) {
    url += '/' + section;
    if (tabKey) {
      url += '/' + tabKey;
    }
  }
  return url;
};

const parseSettingsUrl = (pathname) => {
  const match = pathname.match(/^\/quote-builder\/settings(?:\/([^/]+))?(?:\/([^/]+))?$/);
  if (match) {
    return { section: match[1] || null, tab: match[2] || null };
  }
  return { section: null, tab: null };
};

const Settings = ( {config, user_unit_price_key, edit_id, edit_attribute_id, new_key, tab, show_new_attribute_form} ) => {
  const [activeItem, setActiveItem] = useState(isMobile() ? undefined : (new_key || user_unit_price_key || getCookie('quote_builder_default_settings_item') || "carton_material") );
  const [activeTab, setActiveTab] = useState(tab || null);
  const [editIdToPass, setEditIdToPass] = useState(edit_id ? parseInt(edit_id, 10) : null);
  // Deep-linked attribute edit: /quote-builder/settings/:key/:tab/:id/edit reopens the attribute's
  // edit form directly. Distinct from edit_id, which deep-links a unit PRICE, not an attribute.
  const [editAttributeIdToPass, setEditAttributeIdToPass] = useState(edit_attribute_id || null);
  const [showNewForm, setShowNewForm] = useState(!!new_key);
  const [showNewAttributeForm, setShowNewAttributeForm] = useState(!!show_new_attribute_form);
  const [isLoadingSection, setIsLoadingSection] = useState(!!edit_id);

  const hasSetInitialUrl = useRef(false);
  // Track if section change is from user menu interaction vs initial load
  const isUserMenuNavigation = useRef(false);
  // Track if initial scroll has happened to prevent duplicate scrolls
  const hasInitialScrolled = useRef(false);

  // Fetch the section key for the edit_id and set the active menu item
  useEffect(() => {
    let isCancelled = false;

    if (edit_id && !user_unit_price_key) {
      setIsLoadingSection(true);
      GetRequest({
        url: `/quote-builder/user_unit_prices/${edit_id}/section_key`,
        callback: (data) => {
          if (isCancelled) return;
          if (data.key) {
            setActiveItem(data.key);
          }
          setIsLoadingSection(false);
        },
        404: () => {
          if (isCancelled) return;
          setIsLoadingSection(false);
          // If not found, redirect to settings without edit
          window.history.replaceState({}, '', '/quote-builder/settings');
        }
      });
    }

    return () => {
      isCancelled = true;
    };
  }, [edit_id, user_unit_price_key]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const { section, tab: tabFromUrl } = parseSettingsUrl(window.location.pathname);
      const resolvedSection = section || getCookie('quote_builder_default_settings_item') || 'carton_material';
      setActiveItem(resolvedSection);
      setActiveTab(tabFromUrl);
      setEditIdToPass(null);
      setEditAttributeIdToPass(null);
      setShowNewForm(false);
      setShowNewAttributeForm(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Set the URL on initial load (replaceState so no extra history entry)
  useEffect(() => {
    if (!hasSetInitialUrl.current && activeItem && !edit_id && !edit_attribute_id && !new_key && !showNewAttributeForm) {
      window.history.replaceState({}, '', buildSettingsUrl(activeItem, activeTab));
      hasSetInitialUrl.current = true;
    }
  }, [activeItem]);

  const handleSetActiveItem = (item, tabKey = null) => {
    isUserMenuNavigation.current = true;
    setActiveItem(item);
    setActiveTab(tabKey || null);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Leaving a tab drops any deep-linked edit/new intent, so a stale id can't be re-applied to the
    // tab we switch to (which would open an edit form for a record of the wrong attribute type).
    setEditIdToPass(null);
    setEditAttributeIdToPass(null);
    setShowNewForm(false);
    setShowNewAttributeForm(false);
    const newUrl = buildSettingsUrl(activeItem, tabKey);
    if (window.location.pathname !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
  };

  useEffect(() => {
    // Only scroll on initial load or when user navigates via menu
    // Don't scroll when activeItem changes due to other reasons (prevents scroll jump when popups open)
    if (!hasInitialScrolled.current || isUserMenuNavigation.current) {
      $("html, body").animate({ scrollTop: 100 }, "slow");
      hasInitialScrolled.current = true;
    }
    // Clear edit/new states when user navigates via menu (not initial load)
    if (isUserMenuNavigation.current) {
      setEditIdToPass(null);
      setEditAttributeIdToPass(null);
      setShowNewForm(false);
      setShowNewAttributeForm(false);
      window.history.pushState({}, '', buildSettingsUrl(activeItem, activeTab));
      isUserMenuNavigation.current = false;
    }
    // Depend on activeTab too: switching between a section's two menu lines changes
    // only activeTab, and that still needs the URL written and the nav flag cleared.
    // In-page tab clicks (handleTabChange) leave the flag false, so this is a no-op
    // for them (they push their own URL).
  }, [activeItem, activeTab])

  if (isLoadingSection) {
    return (
      <div className="row" style={{"marginLeft": "-30px"}}>
        <div className="col-lg-12 text-center">
          <p>{t('quote_builder.please_wait')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row" style={{"marginLeft": "-30px"}}>
      <div className="col-lg-3 SettingsMenu">
        <SettingsMenu activeItem={activeItem} activeTab={activeTab} setActiveItem={handleSetActiveItem} />
      </div>
      <div className="col-lg-9 SettingsMenuMainCont">
        {activeItem == "carton_material" && <SettingsTabs config={config.user_unit_prices.carton_material}
          breadcrumbs="materials,menu_material_cartons" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "hard_cardboard_material" && <SettingsTabs config={config.user_unit_prices.hard_cardboard_material}
          breadcrumbs="materials,menu_material_hard_cardboards" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "corrugated_cardboard_material" && <SettingsTabs config={config.user_unit_prices.corrugated_cardboard_material}
          breadcrumbs="materials,menu_material_corrugated_cardboards" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "pvc_material" && <SettingsTabs config={config.user_unit_prices.pvc_material}
          breadcrumbs="materials,menu_material_pvc" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "coating_material" && <SettingsTabs config={config.user_unit_prices.coating_material}
          breadcrumbs="materials,menu_material_coating" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "hot_stamping_material" && <SettingsTabs config={config.user_unit_prices.hot_stamping_material}
          breadcrumbs="menu_hot_stamping,menu_materials" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "hot_stamping_printing_block" && <SettingsTabs config={config.user_unit_prices.hot_stamping_printing_block}
          breadcrumbs="menu_hot_stamping,menu_printing_blocks" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "hot_stamping_machine" && <SettingsTabs config={config.user_unit_prices.hot_stamping_machine}
          breadcrumbs="menu_hot_stamping,menu_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "cellophane_material" && <SettingsTabs config={config.user_unit_prices.cellophane_material}
          breadcrumbs="menu_cellophane,menu_materials" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "cellophane_machine" && <SettingsTabs config={config.user_unit_prices.cellophane_machine}
          breadcrumbs="menu_cellophane,menu_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "printing_machine" && <SettingsTabs config={config.user_unit_prices.printing_machine}
          breadcrumbs="menu_print,printing_machine" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "printing_and_die_cutting_machine" && <SettingsTabs config={config.user_unit_prices.printing_and_die_cutting_machine}
          breadcrumbs="menu_printing_and_die_cutting,menu_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "guillotine_machine" && <SettingsTabs config={config.user_unit_prices.guillotine_machine}
          breadcrumbs="guillotine_cutting,menu_guillotine_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "die_cutting_machine" && <SettingsTabs config={config.user_unit_prices.die_cutting_machine}
          breadcrumbs="menu_cutting,menu_die_cutting_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "metal_strip" && <SettingsTabs config={config.user_unit_prices.metal_strip}
          breadcrumbs="menu_cutting,menu_metal_strips" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "cutting_board" && <SettingsTabs config={config.user_unit_prices.cutting_board}
          breadcrumbs="menu_cutting,menu_cutting_boards" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "glueing_machine" && <SettingsTabs config={config.user_unit_prices.glueing_machine}
          breadcrumbs="glueing,glueing_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "glueing_manual_labor" && <SettingsTabs config={config.user_unit_prices.glueing_manual_labor}
          breadcrumbs="glueing,glueing_manual_labor" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "glueing_material" && <SettingsTabs config={config.user_unit_prices.glueing_material}
          breadcrumbs="glueing,glueing_materials" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "lamination_machine" && <SettingsTabs config={config.user_unit_prices.lamination_machine}
          breadcrumbs="lamination,lamination_machines" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "lamination_manual_labor" && <SettingsTabs config={config.user_unit_prices.lamination_manual_labor}
          breadcrumbs="lamination,lamination_manual_labor" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "magnet_material" && <SettingsTabs config={config.user_unit_prices.magnet_material}
          breadcrumbs="menu_magnet,menu_magnets" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "coating_machine" && <SettingsTabs config={config.user_unit_prices.coating_machine}
          breadcrumbs="menu_coating_labour,coating_machine" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

        {activeItem == "coating_manual_labor" && <SettingsTabs config={config.user_unit_prices.coating_manual_labor}
        breadcrumbs="menu_coating_labour,coating_manual_labor" editId={editIdToPass} editAttributeId={editAttributeIdToPass} showNewForm={showNewForm} activeTab={activeTab} onTabChange={handleTabChange} showNewAttributeForm={showNewAttributeForm} setActiveItem={handleSetActiveItem} /> }

      </div>
    </div>
  )
}

export default Settings;
